import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useAppStore } from "@/store/app/slice";
import type { ILoanOfficer } from "@/types/users-loan-officers";
import { LoanOfficersRepository } from "@/api/loan-officers/repository";
import { LoanOfficersService } from "@/api/loan-officers/service";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

export const useFetchLoanOfficerProfile = () => {
  const abortController = useRef<AbortController | null>(null);
  const user = useAppStore((s) => s.user);
  const [profile, setProfile] = useState<ILoanOfficer | null>(null);

  const fetchProfile = useCallback(() => {
    if (!user) {
      return;
    }

    const loanOfficersRepository = new LoanOfficersRepository(
      new LoanOfficersService(),
    );

    abortController.current?.abort();
    abortController.current = new AbortController();
    const signal = abortController.current.signal;

    loanOfficersRepository
      .getProfileByUserId(
        {
          userId: user.id,
        },
        { signal },
      )
      .then((response) => {
        setProfile(response);
      })
      .catch(serverErrorHandler());
  }, [user]);

  useEffect(() => {
    const debouncedFetch = debounce(fetchProfile, 300);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
      abortController.current?.abort();
    };
  }, [fetchProfile]);

  return {
    fetchProfile,
    profile,
  };
};
